
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ArrowLeft } from "lucide-react";
import DeviceHeader from "@/components/device/DeviceHeader";
import DeviceTabs from "@/components/device/DeviceTabs";
import { type Device } from "@/hooks/useDevices";
import { generateMockDevices } from "@/data/mockDevices";
import { getDeviceStatus } from "@/utils/deviceStatus";
import { isValidLastKnownData, isValidInstalledApp } from "@/types/device";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useNavigationContext } from "@/hooks/useNavigationContext";
import { BreadcrumbNavigation } from "@/components/navigation/BreadcrumbNavigation";
import { useState, useRef } from "react";

const DeviceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getBackNavigation } = useNavigationContext();
  const [activeTab, setActiveTab] = useState("info");
  const tabsRef = useRef<HTMLDivElement>(null);
  
  const { data: device, isLoading, error } = useQuery({
    queryKey: ['device', id],
    queryFn: async () => {
      if (!id) {
        throw new Error('Device ID is required');
      }
      
      console.log('Fetching device details for ID:', id);
      
      try {
        // First check if the ID is a valid UUID before querying Supabase
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const isUuid = uuidRegex.test(id);
        
        // If it's a UUID, try to fetch from the database
        if (isUuid) {
          const { data: dbDevice, error: dbError } = await supabase
            .from('devices')
            .select(`
              *,
              installed_apps!installed_apps_device_id_fkey (
                id,
                name,
                version,
                installed_date,
                bundle_id,
                icon
              ),
              script_deployments (
                id,
                status,
                last_run,
                exit_code,
                output,
                retry_enabled,
                configurations (
                  name
                )
              )
            `)
            .eq('id', id)
            .maybeSingle() as { data: any; error: any };
          
          if (dbError) {
            console.error('Database error:', dbError);
            throw dbError;
          }
          
          if (dbDevice) {
            // Process the database device
            const rawLastKnownData = dbDevice.last_known_data as unknown;
            const lastKnownData = isValidLastKnownData(rawLastKnownData) ? rawLastKnownData : null;
            
            const calculatedStatus = getDeviceStatus(dbDevice.last_seen || dbDevice.created_at, dbDevice.status);
            
            // Get installed apps from the joined installed_apps table
            const installedApps = Array.isArray(dbDevice.installed_apps) 
              ? dbDevice.installed_apps.map((app: any) => ({
                  name: app.name,
                  version: app.version || null,
                  installed_date: app.installed_date,
                  bundle_id: app.bundle_id || undefined,
                  icon: app.icon || undefined
                })) 
              : [];
            
            const deviceData: Device = {
              id: dbDevice.id,
              name: dbDevice.name || dbDevice.hostname,
              hostname: dbDevice.hostname,
              os_type: dbDevice.os_type,
              os_version: dbDevice.os_version ?? lastKnownData?.os_version ?? dbDevice.os_version,
              status: calculatedStatus,
              last_seen: dbDevice.last_seen,
              created_at: dbDevice.created_at,
              serial_number: dbDevice.serial_number,
              ip_address: dbDevice.ip_address ?? lastKnownData?.ip_address ?? null,
              battery_level: dbDevice.battery_level ?? lastKnownData?.battery_level ?? null,
              battery_status: dbDevice.battery_status ?? lastKnownData?.battery_status ?? null,
              installed_apps: installedApps,
              deviceAdditionalProperties: dbDevice.deviceAdditionalProperties as any,
              department: null,
              location: null,
              script_deployments: dbDevice.script_deployments || []
            };
            
            return deviceData;
          }
        }
        
        // If not found in database or not a UUID, check the mock data
        console.log('Device not found in database or not a UUID, using mock data');
        const mockData = generateMockDevices();
        const mockDevice = mockData.find(d => d.id === id);
        
        if (!mockDevice) {
          toast.error('Device not found');
          throw new Error('Device not found');
        }
        
        return mockDevice;
      } catch (fetchError) {
        console.error('Error fetching device details:', fetchError);
        throw fetchError;
      }
    },
    enabled: !!id,
    refetchInterval: 30000,  // Reduce refetch frequency to 30 seconds
    staleTime: 25000,        // Increase stale time
    gcTime: 60000,
    retry: 1                 // Limit retries to reduce endless refreshing on error
  });

  const handleBackClick = () => {
    const backNav = getBackNavigation();
    navigate(backNav?.path || '/devices');
  };

  const handleCardClick = (tab: string) => {
    setActiveTab(tab);
    // Smooth scroll to tabs
    setTimeout(() => {
      tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  if (isLoading) {
    return <div className="min-h-screen bg-white dark:bg-slate-900 p-6">
        <div className="container max-w-7xl mx-auto flex justify-center items-center h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
        </div>
      </div>;
  }
  
  if (error || !device) {
    return <div className="min-h-screen bg-white dark:bg-slate-900 p-6">
        <div className="container max-w-7xl mx-auto text-center text-red-500 p-6">
          {error instanceof Error ? error.message : 'Error loading device details. Please try again later.'}
        </div>
      </div>;
  }
  
  const backNav = getBackNavigation();

  return <div className="h-full flex flex-col">
      <div className="border-b bg-card/30">
        <div className="flex items-center justify-between px-6 py-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleBackClick}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {backNav?.label || 'Back to Devices'}
          </Button>
        </div>
        <div className="px-6 pb-8 pt-4">
          <DeviceHeader device={device} onCardClick={handleCardClick} />
        </div>
      </div>
      <div className="flex-1 overflow-auto px-6 py-6">
        <div ref={tabsRef}>
          <DeviceTabs device={device} activeTab={activeTab} onTabChange={setActiveTab} />
        </div>
      </div>
    </div>;
};

export default DeviceDetail;
